"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import * as DialogPrimitive from "@radix-ui/react-dialog";

type UnsavedChangesPromptProps = {
  active: boolean;
  title?: string;
  message?: string;
  stayLabel?: string;
  leaveLabel?: string;
};

// Warns before the user navigates away from a half-filled form.
export function UnsavedChangesPrompt({
  active,
  title = "Leave without finishing?",
  message = "You haven't submitted the form yet. If you leave now, the details you've entered will be lost.",
  stayLabel = "Continue editing",
  leaveLabel = "Discard and leave",
}: UnsavedChangesPromptProps) {
  const router = useRouter();
  const [pending, setPending] = useState<string | null>(null);

  useEffect(() => {
    if (!active) return;

    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };

    const onClick = (e: MouseEvent) => {
      if (
        e.defaultPrevented ||
        e.button !== 0 ||
        e.metaKey ||
        e.ctrlKey ||
        e.shiftKey ||
        e.altKey
      ) {
        return;
      }
      const anchor = (e.target as HTMLElement)?.closest?.("a");
      if (!anchor || anchor.hasAttribute("download")) return;
      if (anchor.target && anchor.target !== "_self") return;
      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("#")) return;

      let url: URL;
      try {
        url = new URL(anchor.href, window.location.href);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) return;
      // Same page (e.g. in-page anchor), so nothing to lose.
      if (url.pathname === window.location.pathname && url.search === window.location.search) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      setPending(url.pathname + url.search + url.hash);
    };

    window.addEventListener("beforeunload", onBeforeUnload);
    document.addEventListener("click", onClick, true);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      document.removeEventListener("click", onClick, true);
    };
  }, [active]);

  const stay = () => setPending(null);
  const leave = () => {
    const dest = pending;
    setPending(null);
    if (dest) router.push(dest);
  };

  return (
    <DialogPrimitive.Root
      open={pending !== null}
      onOpenChange={(open) => {
        if (!open) stay();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-100 bg-black/60 md:backdrop-blur-[2px]" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className="fixed top-1/2 left-1/2 z-100 w-[min(92vw,420px)] -translate-x-1/2 -translate-y-1/2 rounded-[20px] bg-white p-6 text-center shadow-[0_40px_80px_-24px_rgba(0,0,0,0.35)] outline-none"
        >
          <DialogPrimitive.Title className="text-h6 font-bold text-black">
            {title}
          </DialogPrimitive.Title>
          <p className="text-body mt-2 leading-[1.56] text-black/60">{message}</p>
          <div className="mt-5 flex flex-col gap-3">
            <button
              type="button"
              onClick={stay}
              className="bg-teal-07 hover:bg-teal-08 flex h-12 cursor-pointer items-center justify-center rounded-xl text-sm font-semibold text-white transition-colors"
            >
              {stayLabel}
            </button>
            <button
              type="button"
              onClick={leave}
              className="hover:border-teal-07 hover:text-teal-07 flex h-12 cursor-pointer items-center justify-center rounded-xl border border-black/15 text-sm font-semibold text-black/70 transition-colors"
            >
              {leaveLabel}
            </button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
